import {
    ICubeSourceMetaDataService,
    ICubeSourceMetricId,
    ICubeSourceSearchMetricIdOptions,
} from "./PA.HyperRectangle.Services.IF";
import {
    WB_CubeSourcePerspectiveRow,
    WB_CubeSourceRow,
    WB_MetricDefinitionRow,
    WB_MetricViewRow
} from "../PA.Core/PAdbSchemaRows";
import { GenericLease } from "../PA.Utils/GenericLease";
import { PAdbContext } from "../PA.Core/PAdbContext";
import {
    esWB_CubeSource,
    esWB_CubeSource_DataSource,
    esWB_CubeSource_DataSource_Dimension,
    esWB_CubeSource_Dimension, esWB_CubeSource_Perspective,
    esWB_DataObject,
    esWB_DataObject_Column,
    esWB_DataObject_Column_Text,
    esWB_Metric_Definition,
    esWB_Metric_View,
    esWB_Metric_View_Metric,
    esWB_MetricDefinition_Component,
    esWB_MetricDefinition_EventSpend_Type,
    esWB_MetricDefinition_GLAccount,
} from "../PA.Core/PAdbSchema";
import * as _ from "lodash";
import * as AsyncLock from "async-lock";
import { ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";
import { DefaultHierarchyPolicy } from "./policies/DefaultPolicy";
import {
    ICubeSourceHierarchyPolicy,
    ICubeSourceKeyPolicy,
    ICubeSourceMetricsPolicy,
} from "../PA.HyperRectangle.Core/CubeSource.IF";
import { PlanTypeHierarchyPolicy } from "./policies/PlanTypeHierarchyPolicy";
import { ModelMetricPolicy } from "./policies/ModelMetricPolicy";
import { SimulationInputMetricPolicy } from "./policies/SimulationInputMetricPolicy";
import { ScenarioPlanHierarchyPolicy } from "./policies/ScenarioPlanHierarchyPolicy";
import { PEAPlanHierarchyPolicy } from './policies/PEAPlanHierarchyPolicy'

// In order to improve load performance, this service will cache CubeSource Metadata
// We will use leases to invalidate the caches

interface ICubeSourceCacheEntry {
    wbId: number;
    Id: string;
    CubeSource: WB_CubeSourceRow;
    Metrics: WB_MetricDefinitionRow[];
    MetricViews: WB_MetricViewRow[];
    Profiles: WB_CubeSourcePerspectiveRow[];
    Lease: GenericLease;
}

export class CubeSourceMetaDataService implements ICubeSourceMetaDataService {
    private static dctCubeSourceByWbId: { [wbCubeSourceId: number]: ICubeSourceCacheEntry } = {};
    private static dctCubeSourceById: { [CubeSourceId: string]: number } = {};
    private static cacheLock: AsyncLock = new AsyncLock();
    private static dctHierarchyPolicy: { [policyId: string]: ICubeSourceHierarchyPolicy };
    private static dctMetricPolicy: { [policyId: string]: ICubeSourceMetricsPolicy };
    private static dctKeyPolicy: { [policyId: string]: ICubeSourceKeyPolicy };

    async getCubeSource(wbCubeSourceId: number): Promise<WB_CubeSourceRow> {
        let ce = await CubeSourceMetaDataService.getCacheEntry(wbCubeSourceId);
        if (_.isEmpty(ce)) return undefined;
        return ce.CubeSource;
    }

    async getCubeSourceByID(CubeSourceID: string): Promise<WB_CubeSourceRow> {
        if (CubeSourceID in CubeSourceMetaDataService.dctCubeSourceById)
            return await this.getCubeSource(CubeSourceMetaDataService.dctCubeSourceById[CubeSourceID]);

        let dbContext = new PAdbContext();
        let cubeSourceRows: WB_CubeSourceRow[] = <any>(
            await dbContext.findAll(esWB_CubeSource, {
                where: { CUBESOURCE_ID: CubeSourceID, DELETED_IND: { neq: "1" } },
            })
        );
        if (_.isEmpty(cubeSourceRows)) return undefined;
        let cubeSourceRow = _.first(cubeSourceRows);

        return await this.getCubeSource(cubeSourceRow.cif.WB_CUBESOURCE_ID);
    }

    async getCubeSourceMetricViews(wbCubeSourceId: number): Promise<WB_MetricViewRow[]> {
        let ce = await CubeSourceMetaDataService.getCacheEntry(wbCubeSourceId);
        if (_.isEmpty(ce)) return [];
        return ce.MetricViews;
    }

    async getCubeSourceProfiles(wbCubeSourceId: number): Promise<WB_CubeSourcePerspectiveRow[]> {
        let ce = await CubeSourceMetaDataService.getCacheEntry(wbCubeSourceId);
        if (_.isEmpty(ce)) return [];
        return ce.Profiles;
    }

    async getCubeSourceMetrics(wbCubeSourceId: number): Promise<WB_MetricDefinitionRow[]> {
        let ce = await CubeSourceMetaDataService.getCacheEntry(wbCubeSourceId);
        if (_.isEmpty(ce)) return [];
        return ce.Metrics;
    }

    private static async getCacheEntry(wbCubeSourceId: number): Promise<ICubeSourceCacheEntry> {
        // We could lock at a lower level, i.e. wbCubeSourceId
        let lockKey = "cubeSource-" + wbCubeSourceId;

        return CubeSourceMetaDataService.cacheLock
            .acquire(lockKey, async (): Promise<ICubeSourceCacheEntry> => {
                let ce: ICubeSourceCacheEntry = undefined;

                if (wbCubeSourceId in CubeSourceMetaDataService.dctCubeSourceByWbId) {
                    ce = CubeSourceMetaDataService.dctCubeSourceByWbId[wbCubeSourceId];
                    let bDataChanged = await ce.Lease.hasDataChanged();
                    if (!bDataChanged) return ce;
                }

                // Load up the Cube Source & Metrics with the Record Resolver
                let dbContext = new PAdbContext();
                let rr = new ESRecordResolver(dbContext, {
                    standardConditions: {
                        [esWB_CubeSource]: { DELETED_IND: { neq: "1" } },
                        [esWB_CubeSource_Dimension]: { DELETED_IND: { neq: "1" } },
                        [esWB_CubeSource_DataSource]: { DELETED_IND: { neq: "1" } },
                        [esWB_CubeSource_DataSource_Dimension]: { DELETED_IND: { neq: "1" } },
                        [esWB_Metric_Definition]: { DELETED_IND: { neq: "1" } },
                        [esWB_Metric_View]: { DELETED_IND: { neq: "1" } },
                        [esWB_Metric_View_Metric]: { DELETED_IND: { neq: "1" } },
                        [esWB_CubeSource_Perspective]: { DELETED_IND: { neq: "1" } },
                    },
                    dctIncludeBelongsToNamedRelations: {
                        [esWB_DataObject]: [esWB_DataObject_Column],
                    },
                    dctExcludeHasManyNamedRelations: {
                        [esWB_DataObject_Column]: [esWB_DataObject_Column_Text],
                        [esWB_Metric_Definition]: [
                            esWB_MetricDefinition_GLAccount,
                            esWB_MetricDefinition_EventSpend_Type,
                            esWB_MetricDefinition_Component,
                        ],
                    },
                });

                // Read in Cube Source
                await rr.findAllWithRelationships(esWB_CubeSource, [wbCubeSourceId]);
                let cubeSourceRow: WB_CubeSourceRow = <any>rr.get(esWB_CubeSource, wbCubeSourceId);

                if (_.isEmpty(cubeSourceRow)) return undefined;

                // Read in Metric Definition
                let metrics: WB_MetricDefinitionRow[] = <any>await rr.queryAllWithRelationships(
                    esWB_Metric_Definition,
                    {
                        WB_CUBESOURCE_ID: wbCubeSourceId,
                        DELETED_IND: { neq: "1" },
                    },
                );

                let metricViews: WB_MetricViewRow[] = <any>await rr.queryAllWithRelationships(esWB_Metric_View, {
                    WB_CUBESOURCE_ID: wbCubeSourceId,
                    DELETED_IND: { neq: "1" },
                });

                let profiles: WB_CubeSourcePerspectiveRow[] = <any>await rr.queryAllWithRelationships(esWB_CubeSource_Perspective, {
                    WB_CUBESOURCE_ID: wbCubeSourceId,
                    DELETED_IND: { neq: "1" },
                });


                if (_.isEmpty(ce)) {
                    ce = {
                        wbId: cubeSourceRow.cif.WB_CUBESOURCE_ID,
                        Id: cubeSourceRow.cif.CUBESOURCE_ID,
                        CubeSource: cubeSourceRow,
                        Metrics: metrics,
                        MetricViews: metricViews,
                        Profiles: profiles,
                        Lease: new GenericLease(LeaseRegistry.Lease_Type_CubeSource + ":" + wbCubeSourceId),
                    };
                    ce.Lease.addClass(LeaseRegistry.Lease_Type_CubeSource);
                    ce.Lease.addClass(LeaseRegistry.Lease_Type_DataObject);
                    ce.Lease.addClass(LeaseRegistry.Lease_Type_Metrics);
                    ce.Lease.addClass(LeaseRegistry.Lease_Type_MetricView);
                    ce.Lease.addClass(LeaseRegistry.Lease_Type_CubeSource_Profile);
                    CubeSourceMetaDataService.dctCubeSourceByWbId[ce.wbId] = ce;
                    CubeSourceMetaDataService.dctCubeSourceById[ce.Id] = ce.wbId;
                } else {
                    ce.Id = cubeSourceRow.cif.CUBESOURCE_ID;
                    ce.CubeSource = cubeSourceRow;
                    (ce.Metrics = metrics), (ce.MetricViews = metricViews), (ce.Profiles = profiles), ce.Lease.resetLease();
                    CubeSourceMetaDataService.dctCubeSourceById[ce.Id] = ce.wbId;
                }

                return ce;
            })
            .then((results: ICubeSourceCacheEntry) => {
                return results;
            });
    }

    getCubeSourceHierarchyPolicies(): { [p: string]: ICubeSourceHierarchyPolicy } {
        if (_.isUndefined(CubeSourceMetaDataService.dctHierarchyPolicy)) {
            CubeSourceMetaDataService.dctHierarchyPolicy = {};
            const defaultPolicy = new DefaultHierarchyPolicy();
            CubeSourceMetaDataService.dctHierarchyPolicy[defaultPolicy.PolicyId] = defaultPolicy;
            const planTypePolicy = new PlanTypeHierarchyPolicy();
            CubeSourceMetaDataService.dctHierarchyPolicy[planTypePolicy.PolicyId] = planTypePolicy;
            const scnPlanPolicy = new ScenarioPlanHierarchyPolicy();
            CubeSourceMetaDataService.dctHierarchyPolicy[scnPlanPolicy.PolicyId] = scnPlanPolicy;
            const peaPlanPolicy = new PEAPlanHierarchyPolicy();
            CubeSourceMetaDataService.dctHierarchyPolicy[peaPlanPolicy.PolicyId] = peaPlanPolicy;
        }
        return CubeSourceMetaDataService.dctHierarchyPolicy;
    }

    getCubeSourceMetricPolicies(): { [p: string]: ICubeSourceMetricsPolicy } {
        if (_.isUndefined(CubeSourceMetaDataService.dctMetricPolicy)) {
            CubeSourceMetaDataService.dctMetricPolicy = {};
            const modelPolicy = new ModelMetricPolicy();
            CubeSourceMetaDataService.dctMetricPolicy[modelPolicy.PolicyId] = modelPolicy;
            const simulationInputPolicy = new SimulationInputMetricPolicy();
            CubeSourceMetaDataService.dctMetricPolicy[simulationInputPolicy.PolicyId] = simulationInputPolicy;
        }
        return CubeSourceMetaDataService.dctMetricPolicy;
    }

    getCubeSourceKeyPolicies(): { [p: string]: ICubeSourceKeyPolicy } {
        if (_.isUndefined(CubeSourceMetaDataService.dctKeyPolicy)) {
            CubeSourceMetaDataService.dctKeyPolicy = {};
        }
        return CubeSourceMetaDataService.dctKeyPolicy;
    }

    async getCubeSourceMetricsIds(
        wbCubeSourceId: number,
        options?: ICubeSourceSearchMetricIdOptions,
    ): Promise<ICubeSourceMetricId[]> {
        let opts = options || {};

        let cubeSource = await this.getCubeSource(wbCubeSourceId);
        if (_.isEmpty(cubeSource)) return [];

        let rv: ICubeSourceMetricId[] = [];
        _.forEach(cubeSource.WB_CubeSourceDataSourceRows, (aDSR) => {
            if (!aDSR.isActive) return;
            if (!aDSR.WB_DataObjectRow) return;
            if (!aDSR.WB_DataObjectRow.WB_DataObjectColumnRows.length) return;

            _.forEach(aDSR.WB_DataObjectRow.WB_DataObjectColumnRows, (aRow) => {
                if (!aRow.isActive) return;

                if (aRow.cif.COLUMN_METATYPE != "metric") return;

                if (_.isEmpty(aRow.cif.METRIC_AGGR_METHOD) && !!opts.bColumnMetricHasAggregator) return;

                if (!aRow.bMetricVisibleInd && !!opts.bVisibleOnly) return;

                if (!aRow.bMetricEditableInd && !!opts.bEditableOnly) return;

                if (!(aRow.bMetricWritebackEnabledInd || aRow.bMetricEditableInd) && !!opts.bWriteBackOnly) return;

                let metricId = `${aDSR.cif.ALIAS_ID}_${aRow.cif.COLUMN_ID}`;
                let metricName = aRow.cif.COLUMN_NAME;
                let technicalName = `"${aDSR.WB_DataObjectRow.cif.DATAOBJECT_ID}"."${aRow.cif.COLUMN_ID}"`;

                rv.push({
                    MetricId: metricId,
                    MetricName: metricName,
                    TechnicalName: technicalName,
                    Comments: technicalName,
                    GroupName: aRow.cif.METRIC_GROUP || "",
                    Version: aRow.cif.METRIC_VERSION || "",
                    UOMNum: aRow.cif.UOM_NUM,
                    UOMConverterCSVNum: aRow.cif.UOM_CONVERTER_NUM,
                    UOMDenom: aRow.cif.UOM_DENOM,
                    UOMConverterCSVDenom: aRow.cif.UOM_CONVERTER_DENOM,
                    WB_CubeSourceDataSourceRow: aDSR,
                    WB_DataObjectColumnRow: aRow,
                });
            });
        });

        if (!!opts.bColumnMetricsOnly) return rv;

        let metrics = await this.getCubeSourceMetrics(wbCubeSourceId);
        _.forEach(metrics, (aMtrDfn) => {
            if (!aMtrDfn.isActive) return;

            if (aMtrDfn.cif.METRIC_TYPE == "csRow" && !!opts.bExcludeMtrDfnRowCalcLevel) return;

            if (aMtrDfn.cif.METRIC_TYPE == "csPerspective" && !!opts.bExcludeMtrDfnPerspectiveCalcLevel) return;

            if (!aMtrDfn.bVisible && !!opts.bVisibleOnly) return;

            if (!aMtrDfn.bEditable && !!opts.bEditableOnly) return;

            rv.push({
                MetricId: aMtrDfn.cif.METRIC_ID,
                MetricName: aMtrDfn.cif.METRIC_NAME,
                TechnicalName: aMtrDfn.cif.FORMULA,
                Comments: aMtrDfn.cif.METRIC_NOTES,
                Version: aMtrDfn.cif.METRIC_VERSION,
                GroupName: aMtrDfn.cif.METRIC_GROUP,
                UOMNum: aMtrDfn.cif.UOM_NUM,
                UOMConverterCSVNum: aMtrDfn.cif.UOM_CONVERTER_NUM,
                UOMDenom: aMtrDfn.cif.UOM_DENOM,
                UOMConverterCSVDenom: aMtrDfn.cif.UOM_CONVERTER_DENOM,
                WB_MetricDefinitionRow: aMtrDfn,
            });
        });

        return rv;
    }
}
